/* tslint:disable */
/* eslint-disable */
/**
 * REAP API
 * Public REST API for REAP (multi-tenant, agentic AI for network operations).  ### Identifier conventions - **id**: UUID — canonical and used for foreign keys and writes. - **slug**: immutable, human-readable handle (kebab-case) used in configs/URLs. Unique per scope (org) or globally (orgs). - **name**: human-friendly display label (mutable). Do **not** use as a reference key.  ## Authentication Overview  This API supports local credentials (password, passkeys), SSO (OIDC/SAML), and email-verified bootstraps for registration/reset/invitations. The design is enumeration-resistant and minimizes clicks for the common case (daily sign-in).  ### Daily sign-ins (no emailTx required) - **Password**: `POST /auth/password/verify` with `{ email, password }` → returns **loginTx**. - **Passkey (email-first)**: `POST /auth/passkeys/assert` with `{ email }` → WebAuthn options + `challengeToken`   → `POST /auth/passkeys/assert/verify` → **loginTx**. - **Usernameless passkey** (optional): `POST /auth/passkeys/assert` with `{ usernameless: true }`.  Exchange **loginTx** at `POST /auth/token`: - If already org-bound (SSO, single org, or server inference) → returns **TokenResponse**. - If not org-bound and multiple orgs exist → returns **200** with `{ nextStep: \"pickOrg\", memberships:[...] }`.   The server **SHOULD filter out ineligible orgs** so the client can re-call `/auth/token` with `orgId`   without additional policy checks. - To **switch orgs** later, call `POST /auth/token` with `grantType:\"refresh\"` and `orgId`.  ### SSO (OIDC/SAML) - IdP flows produce **`oidcTx`** or **`samlTx`**, which you exchange at `POST /auth/token`. - HRD (home realm discovery): `POST /auth/discover/start` always replies **200** with a discriminated   `nextStep`:     - `sso_redirect` (single, safe target) — redirect using `connection.startUrl` (optionally include `login_hint`).     - `picker` (multiple targets) — render an org/connection picker.     - `verify_email` — prove mailbox control before revealing org/connection detail.   *No membership/policy status leaks at Start; the rich, email-proven view is returned by `/auth/discover/verify`.*  Email verification is a common bootstrap across Discovery and Invitations. Invitation Accept returns emailTx plus invited-org context (policy and SSO connections) without enumerating other orgs. Clients should drive UX from these fields and exchange loginTx/oidcTx/samlTx at `/auth/token`.  ### Email-verified bootstraps (keep `emailTx`) - **Register password/passkey**, **reset password**, and **invitation acceptance** require proving email ownership.   After `/auth/discover/verify` (or `/auth/invitations/accept`) you get **`emailTx`** for these operations or to   obtain a limited pre-org session at `/auth/token` (for example, to create the first org).  ### Enumeration resistance & security - Return generic errors for failed sign-in; normalize response timing and apply rate-limits with small jitter. - Avoid revealing user/org status or policy in pre-verification responses. - Use HTTPS; set appropriate cache controls; consider device-bound refresh tokens and short-lived access tokens.  ### Client authentication REAP supports two client auth mechanisms:  - **Bearer JWT** (`Authorization: Bearer <jwt>`): short-lived, user/session tokens returned by `/auth/token`.   Backends validate these locally via signature/claims.  - **API Key** (`X-Api-Key: <secret>`): long-lived, org-scoped secrets intended for automation (CI/CD, headless jobs).   Keys are validated by REAP’s auth layer; they are not JWTs and should not be sent as Bearer tokens.  **Guidance** - Prefer **Bearer JWTs** for interactive UIs and short-lived clients. - Use **API keys** for machine-to-machine integrations; rotate or **PATCH** `expiresAt` to extend, and **DELETE** (revoke) when done. - Assign permissions via **roles**: provide `roleIds` on create/patch; responses include `rolesResolved` for admin UX. - Do not place API keys in URLs or logs. Keys are shown **once** on creation.  ### API key return semantics - The full API key **secret is returned only in the 201 Create response** of `/orgs/{orgId}/api-keys`. - It is **never** returned by GET/PATCH/DELETE. - Clients should display it once and prompt the operator to store it securely (password manager, vault). - For subsequent requests, present the key as `X-Api-Key: <secret>`; do **not** use the Bearer scheme for API keys.  ## Auth policies (overview) - **SSO JIT (just-in-time) provisioning** can be enabled per org: SSO users can sign in without invitations. - **Invite-only SSO** (optional): require an invitation before SSO users can join the org. - **Local accounts** (optional): allow bootstrap/break-glass users without SSO. - **Discover** avoids user enumeration; org memberships and sign-in options are revealed only after email verification. - **Invitations** never expose tokens/links via API; the backend emails the invite link to the recipient. - **Org auth policy** is a **singleton** at `/orgs/{orgId}/auth/policy` that controls JIT, invite-only SSO, and local-account allowances.   The policy is retrieved/updated via **GET/PUT** and versioned with **ETag**.  ## Discovery result (structure) - **Local methods are global**: `DiscoverVerifyResponse.localConnections` lists user-level methods (`passkey`, `password`) available for the verified email — shown **once** in the UI. - **SSO is per-org**: `memberships[].connections` lists **org-scoped** SSO options (`oidc`, `saml`). This array may be **empty** if the org has no SSO. - **Policy hint**: `memberships[].policy.requiresSso` indicates that the org **requires** SSO (local login should not grant access there), even if local methods exist globally.  ## Passwordless authentication (overview) - New-user and invitation flows converge on **email verification** → short-lived **`emailTx`** (no user enumeration). - Clients then use **Passkeys** (WebAuthn/FIDO2) to enroll a credential (attestation) or sign in (assertion). - Successful passkey assertion yields **`loginTx`**, which is exchanged at **`POST /auth/token`** with `grantType=login_tx`. - **Token grants** supported at `/auth/token`:     - **authorization_code** (OIDC) via **`oidcTx`**     - **saml2_bearer** (SAML) via **`ssoTx`**     - **login_tx** (Passkey) via **`loginTx`**     - **refresh_token** (rotation; optional `orgId` switch)     - **email_tx** (bootstrap pre-org; limited to safe endpoints like creating the first org) - **Stateless challenge** option for passkeys: options responses include a signed **`challengeToken`** that the client must echo on verify,   allowing the server to validate the challenge/guardrails **without storing per-challenge state** (short TTL recommended). - **Optional fast-path**: `POST /auth/passkeys/attest/verify?issueLoginTx=true` may return `loginTx` immediately after registration   (policy permitting), otherwise the default flow is attest → assert → token. - **Usernameless (passkey-first) sign-in**: the portal may skip email/Discover and call   `POST /auth/passkeys/assert` with `{ \"usernameless\": true }`. The server returns   `PublicKeyCredentialRequestOptions` with an empty/omitted `allowCredentials`. The browser’s   Conditional UI prompts if a matching passkey exists; on success, the client verifies at   `/auth/passkeys/assert/verify` to obtain `loginTx`, then exchanges at `/auth/token`.  ## API response conventions - **No envelopes**: endpoints return typed bodies directly. - **Resources**: single GET/POST returns the resource object (composed from `ResourceBase` and `OrgScoped` when applicable). - **Operational endpoints** (e.g., auth/token, passkeys) return typed operation results (no `ResourceBase`). - **Lists**: return `{ items: [...], meta: { ...} }`, where each item is a full resource object and `meta` follows **PageMeta**. Responses MAY include `Link` headers (`rel=\"next\"`, `rel=\"prev\"`). When `meta.exactTotal` is omitted clients SHOULD treat totals as unknown and render “more available” messaging rather than fabricating counts.  ### Cache-Control conventions - **Mutable resource detail** (single GET, POST, PATCH) and every response that carries credentials, tokens, or bootstrap data sends `Cache-Control: no-store`. Browsers MUST revalidate before rendering drawers/forms so operators see their latest edits. - **Collection/list endpoints** that back dashboards or tables may use `Cache-Control: private, max-age=15` together with `ETag` and `Link` headers. Clients SHOULD revalidate on manual refresh (If-None-Match) and treat the TTL as an upper bound. - **Streaming/event feeds** and download endpoints for bootstrap bundles always respond with `Cache-Control: no-store`. - If a response omits `Cache-Control`, treat it as non-cacheable.  ### Identity & scoping - Every object is keyed by `id` (UUID). - Common audit fields come from `ResourceBase` (`createdAt`, `updatedAt`, `createdBy`, `updatedBy`). - Multi-tenant scoping builds on `OrgScoped` (adds `orgId`); request bodies never include `orgId`—it comes from the path.  ### Pagination & sorting - Pagination via `limit` (1–200, default 50), `after`, `before`. Servers SHOULD return `Link` headers that mirror `meta.next`/`meta.prev` and include `meta.exactTotal` when a precise count is available. - `meta.exactTotal` is OPTIONAL. When omitted it signals “total unknown”; when the repository already computes a count (e.g., `COUNT(*)`) servers SHOULD populate it. - Sort (when supported) is a comma-separated list; prefix with `-` for descending (for example `-lastSeenAt,mgmtAddress`).  ### Concurrency & caching - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match** (mismatch → **412**). - `GET` supports **If-None-Match** and may return **304 Not Modified**. - Each response includes **`X-Request-Id`** for troubleshooting.  ### Conditional GETs (ETag / If-None-Match) - Servers return **ETag** headers on GETs for cache-friendly revalidation. - Clients can send **If-None-Match** with the last seen ETag:   - If representation is unchanged → **304 Not Modified** (no body).   - If changed → **200 OK** with a new ETag and the latest representation. - Recommended for frequently-polled resources (e.g., devices list, discovery runs, topology graphs).  ### Error model - Structured errors use the `Problem` schema. - Common statuses: **400** (validation), **401/403** (auth/authz), **404** (not found), **409** (conflict), **412** (ETag mismatch), **429** (rate limit).  ### Credentials model (overview) - **Secret vs Credential vs Assignment**   - **Secret**: write-only value + metadata (`type`, `fingerprint`, `last4`). Value is never returned.   - **Credential**: one access **kind** (`cli_ssh`, `snmp_v2c`, `snmp_v3`, `gnmi`, `api_rest`) with kind-specific fields that **reference Secret IDs** (e.g., SSH key, SNMP passwords, API key, mTLS certs).   - **Credential Assignment**: routing rule that applies a **single credential** by scope (org / sites / specific devices) with optional match filters (hostname regex/prefix/suffix, labels, vendor/model).   - Rotate a secret’s value via `POST /value` (use **If-Match** to guard against concurrent rotation). - **Validation**   - On create/update, the server validates that each referenced Secret ID is of the **expected SecretType** for the credential kind (mismatch → 400). - **Resolution order (when multiple assignments match a device)**   1. Lower `priority` first (e.g., 10 wins over 100)   2. Narrower `scope` (device > sites > org)   3. Most recent `updatedAt` - **Consistency**   - Single-resource endpoints return the resource (no envelopes).   - List endpoints return `{ items, meta }` where `meta` follows `PageMeta` and cursors are passed via `limit/after/before`. - **Concurrency & cache**   - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match**.   - `GET` supports **If-None-Match** to return **304 Not Modified** when ETag matches.  ## Devices (key behaviors)  - **States** — `discovered`, `validated`, `unreachable`, `retired`, `merged` (see `DeviceState`). - **Discovery-managed fields** (for example `vendor`, `model`, `osVersion`, `serial`, `hostname`, `firstSeenAt`, `lastSeenAt`) are **read-only** via API. - **User-editable** fields: `displayName`, `labels` (case-insensitive keys; PATCH overwrites the map), `role` (free text, e.g. `access`, `core`, `firewall`, `wireless-ap`), `notes`. - **Create** is allowed (beta/testing). Default list sort: `-lastSeenAt`. - **Retire** via `PATCH` (`state=retired`). **DELETE** is hard-delete (beta convenience). - **Merge** consolidates duplicates; merged-away device IDs subsequently return **404**.  ## Edge connectivity (Connectors & Connector Clusters)  **Connectors** are lightweight agents (AMI/OVA/etc.) that establish outbound, persistent control channels from customer sites to REAP. Connectors register to an **Organization**, can optionally belong to a **Connector Cluster**, and periodically report health, version, and capabilities.  **Connector Clusters** group one or more connectors for redundancy or capacity. Clusters can be attached to **Sites** (N↔M). The API allows make-before-break migrations: a Site can temporarily be served by multiple clusters. Backend scheduling ensures only one active worker set manages a Site at a time. - The attach result includes **multiAttached** to surface Sites now attached to this cluster and at least one other cluster.  **User-data (first-boot)**: each Connector exposes a one-time, short-lived bootstrap bundle for cloud-init. Content negotiation: - `Accept: text/cloud-config` → cloud-init YAML (default) - `Accept: application/json` → JSON wrapper (opaque to clients) - `Accept: application/base64+json` → base64 of the opaque JSON  One-time semantics: redeemed → **409 Conflict**, expired → **410 Gone**. Responses SHOULD include `Cache-Control: no-store`.  **Concurrency & caching**: mutable single-resource endpoints return **ETag** and require **If-Match** on updates/deletes. Reads MAY accept **If-None-Match** and return **304**. Lists use pagination via `{ items, meta }` (see `PageMeta`).  # SSO / IdP Connections REAP supports per-organization **IdP connections** for **OIDC** and **SAML**. - Connections are modeled as a discriminated union (`type: \"oidc\" | \"saml\"`). - Connection **config** holds IdP metadata. Some fields are **readOnly** and computed by REAP (e.g., `redirectUris` for OIDC; `acsUrl`, `spEntityId` for SAML). - **Concurrency:** single-resource GET returns **ETag**; **PATCH**_/_**DELETE** require **If-Match**. - **Deletes** return **204 No Content**. - **SAML IdP-initiated** sign-in can be allowed/blocked by `IdpSamlConfig.allowIdpInitiated` (default **true**). # OIDC client authentication - Confidential clients only: REAP supports client_secret_basic (default) and client_secret_post. - private_key_jwt and public clients are not supported in this version. - PKCE is supported but off by default; enable per connection via `config.enforcePkce`.  ## Local password authentication (optional) - Fits the same **email-first, enumeration-resistant** pattern: verify email → short-lived **`emailTx`**. - Supports:   - **Register** a password after email verification   - **Verify** password to obtain **`loginTx`** (exchange at `/auth/token` with `grantType=loginTx`)   - **Reset** password (email-verified), **Change** password (bearer session) - Client sends the **clear-text password over HTTPS**; the server performs KDF hashing (e.g., **Argon2id** with per-user salt; optional application-level pepper in KMS). - Store the **password hash (and parameters)** in the **regular user table** (not a secrets manager). If you use a **pepper**, store that pepper as a secret. - Error hardening:   - Same **lockout** semantics as passkeys (use **423** with a neutral error body)   - Consistent, non-enumerating responses for all email-bound flows
 *
 * The version of the OpenAPI document: 1.3.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as runtime from '../runtime';
import { CloudAccessConnectionFromJSON, CloudAccessConnectionsListResponseFromJSON, CloudAccessLaunchRequestToJSON, CloudAccessLaunchResponseFromJSON, CloudAccessQuickCreateRequestToJSON, CloudAccessQuickCreateResponseFromJSON, ConnectorClustersListResponseFromJSON, SiteFromJSON, SiteCreateToJSON, SiteIntelligenceNamingFindingDetailResponseFromJSON, SiteIntelligenceNamingListResponseFromJSON, SiteIntelligenceNamingReviewQuestionListResponseFromJSON, SiteIntelligenceRoutingFindingDetailResponseFromJSON, SiteIntelligenceRoutingFindingListResponseFromJSON, SiteIntelligenceRoutingResponseFromJSON, SiteIntelligenceRoutingReviewQuestionListResponseFromJSON, SiteIntelligenceSummaryFromJSON, SiteIntelligenceTopologyFindingDetailResponseFromJSON, SiteIntelligenceTopologyFindingListResponseFromJSON, SiteIntelligenceTopologyResponseFromJSON, SiteIntelligenceTopologyReviewQuestionListResponseFromJSON, SiteIntelligenceVlanDetailResponseFromJSON, SiteIntelligenceVlanFindingDetailResponseFromJSON, SiteIntelligenceVlanListResponseFromJSON, SiteUpdateToJSON, SitesListResponseFromJSON, } from '../models/index';
/**
 *
 */
export class SitesApi extends runtime.BaseAPI {
    /**
     * Ensure the account-level cloud discovery access connection exists and return the AWS CloudFormation quick-create URL.
     * Create cloud access launch URL
     */
    createCloudAccessQuickCreateRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createCloudAccessQuickCreate().');
            }
            if (requestParameters['cloudAccessQuickCreateRequest'] == null) {
                throw new runtime.RequiredError('cloudAccessQuickCreateRequest', 'Required parameter "cloudAccessQuickCreateRequest" was null or undefined when calling createCloudAccessQuickCreate().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloud-access/quick-create`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CloudAccessQuickCreateRequestToJSON(requestParameters['cloudAccessQuickCreateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudAccessQuickCreateResponseFromJSON(jsonValue));
        });
    }
    /**
     * Ensure the account-level cloud discovery access connection exists and return the AWS CloudFormation quick-create URL.
     * Create cloud access launch URL
     */
    createCloudAccessQuickCreate(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createCloudAccessQuickCreateRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Ensures the provider-scoped cloud discovery access connection exists and returns a backend-selected HTTPS URL for the provider\'s onboarding portal. The request never accepts customer cloud credentials, access tokens, template URLs, or redirect URLs.
     * Create a provider onboarding launch URL
     */
    createProviderCloudAccessLaunchRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createProviderCloudAccessLaunch().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling createProviderCloudAccessLaunch().');
            }
            if (requestParameters['cloudAccessLaunchRequest'] == null) {
                throw new runtime.RequiredError('cloudAccessLaunchRequest', 'Required parameter "cloudAccessLaunchRequest" was null or undefined when calling createProviderCloudAccessLaunch().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/{provider}/cloud-access/launch`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CloudAccessLaunchRequestToJSON(requestParameters['cloudAccessLaunchRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudAccessLaunchResponseFromJSON(jsonValue));
        });
    }
    /**
     * Ensures the provider-scoped cloud discovery access connection exists and returns a backend-selected HTTPS URL for the provider\'s onboarding portal. The request never accepts customer cloud credentials, access tokens, template URLs, or redirect URLs.
     * Create a provider onboarding launch URL
     */
    createProviderCloudAccessLaunch(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createProviderCloudAccessLaunchRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns observed hostname-family, variant, attribute, and supporting-device evidence for one snapshot-scoped opaque naming finding identifier.
     * Get Site Intelligence naming finding detail
     */
    getSiteIntelligenceNamingFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceNamingFinding().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceNamingFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling getSiteIntelligenceNamingFinding().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/naming/findings/{findingId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceNamingFindingDetailResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns observed hostname-family, variant, attribute, and supporting-device evidence for one snapshot-scoped opaque naming finding identifier.
     * Get Site Intelligence naming finding detail
     */
    getSiteIntelligenceNamingFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceNamingFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns bounded BGP routing coverage and statement previews for the selected immutable snapshot. Use the paginated routes for every statement, then a finding detail route for typed deterministic evidence.
     * Get compact Site Intelligence routing
     */
    getSiteIntelligenceRoutingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceRouting().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceRouting().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/routing`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceRoutingResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns bounded BGP routing coverage and statement previews for the selected immutable snapshot. Use the paginated routes for every statement, then a finding detail route for typed deterministic evidence.
     * Get compact Site Intelligence routing
     */
    getSiteIntelligenceRouting(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceRoutingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns typed deterministic BGP observation evidence for one snapshot-scoped routing statement or review question. The response never exposes internal observation handles or raw session, address, and ASN payloads.
     * Get Site Intelligence routing finding detail
     */
    getSiteIntelligenceRoutingFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceRoutingFinding().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceRoutingFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling getSiteIntelligenceRoutingFinding().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/routing/findings/{findingId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceRoutingFindingDetailResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns typed deterministic BGP observation evidence for one snapshot-scoped routing statement or review question. The response never exposes internal observation handles or raw session, address, and ASN payloads.
     * Get Site Intelligence routing finding detail
     */
    getSiteIntelligenceRoutingFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceRoutingFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the compact landing projection for one immutable historical Site Intelligence snapshot.
     * Get compact Site Intelligence snapshot
     */
    getSiteIntelligenceSnapshotRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceSnapshot().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceSnapshot().');
            }
            if (requestParameters['snapshotId'] == null) {
                throw new runtime.RequiredError('snapshotId', 'Required parameter "snapshotId" was null or undefined when calling getSiteIntelligenceSnapshot().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/snapshots/{snapshotId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters['snapshotId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceSummaryFromJSON(jsonValue));
        });
    }
    /**
     * Returns the compact landing projection for one immutable historical Site Intelligence snapshot.
     * Get compact Site Intelligence snapshot
     */
    getSiteIntelligenceSnapshot(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceSnapshotRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the latest completed Site Intelligence landing response for one site. This read never starts a materialization. Large VLAN memberships and typed topology evidence are available through snapshot-scoped detail routes; internal Site Insights handles are never exposed.
     * Get latest compact Site Intelligence summary
     */
    getSiteIntelligenceSummaryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceSummary().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceSummary().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/summary`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceSummaryFromJSON(jsonValue));
        });
    }
    /**
     * Returns the latest completed Site Intelligence landing response for one site. This read never starts a materialization. Large VLAN memberships and typed topology evidence are available through snapshot-scoped detail routes; internal Site Insights handles are never exposed.
     * Get latest compact Site Intelligence summary
     */
    getSiteIntelligenceSummary(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceSummaryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns bounded architecture, aggregate observed topology counts, and at most three finding and review-question previews for the selected immutable snapshot. Use the paginated routes for complete statements, then a finding detail route for typed structural evidence.
     * Get compact Site Intelligence topology
     */
    getSiteIntelligenceTopologyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceTopology().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceTopology().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/topology`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceTopologyResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns bounded architecture, aggregate observed topology counts, and at most three finding and review-question previews for the selected immutable snapshot. Use the paginated routes for complete statements, then a finding detail route for typed structural evidence.
     * Get compact Site Intelligence topology
     */
    getSiteIntelligenceTopology(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceTopologyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns typed physical-link, HA-pair, and component evidence for one snapshot-scoped opaque topology finding identifier.
     * Get Site Intelligence topology finding detail
     */
    getSiteIntelligenceTopologyFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceTopologyFinding().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceTopologyFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling getSiteIntelligenceTopologyFinding().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/topology/findings/{findingId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceTopologyFindingDetailResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns typed physical-link, HA-pair, and component evidence for one snapshot-scoped opaque topology finding identifier.
     * Get Site Intelligence topology finding detail
     */
    getSiteIntelligenceTopologyFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceTopologyFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns observed membership, name evidence, cautious purpose, and matching evidence-backed findings for one VLAN in the selected immutable snapshot.
     * Get observed Site Intelligence VLAN detail
     */
    getSiteIntelligenceVlanRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceVlan().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceVlan().');
            }
            if (requestParameters['vlanId'] == null) {
                throw new runtime.RequiredError('vlanId', 'Required parameter "vlanId" was null or undefined when calling getSiteIntelligenceVlan().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/vlans/{vlanId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"vlanId"}}`, encodeURIComponent(String(requestParameters['vlanId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceVlanDetailResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns observed membership, name evidence, cautious purpose, and matching evidence-backed findings for one VLAN in the selected immutable snapshot.
     * Get observed Site Intelligence VLAN detail
     */
    getSiteIntelligenceVlan(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceVlanRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns observed VLAN member-interface and current-name evidence for one snapshot-scoped opaque VLAN finding identifier.
     * Get Site Intelligence VLAN finding detail
     */
    getSiteIntelligenceVlanFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getSiteIntelligenceVlanFinding().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling getSiteIntelligenceVlanFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling getSiteIntelligenceVlanFinding().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/vlans/findings/{findingId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceVlanFindingDetailResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns observed VLAN member-interface and current-name evidence for one snapshot-scoped opaque VLAN finding identifier.
     * Get Site Intelligence VLAN finding detail
     */
    getSiteIntelligenceVlanFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getSiteIntelligenceVlanFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List cloud access connections
     */
    listCloudAccessConnectionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCloudAccessConnections().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            if (requestParameters['accountId'] != null) {
                queryParameters['accountId'] = requestParameters['accountId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloud-access`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudAccessConnectionsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List cloud access connections
     */
    listCloudAccessConnections(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCloudAccessConnectionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Lists connections for a registered cloud provider within the organization.
     * List cloud access connections for a provider
     */
    listProviderCloudAccessConnectionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listProviderCloudAccessConnections().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling listProviderCloudAccessConnections().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            if (requestParameters['accountId'] != null) {
                queryParameters['accountId'] = requestParameters['accountId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/{provider}/cloud-access`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudAccessConnectionsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Lists connections for a registered cloud provider within the organization.
     * List cloud access connections for a provider
     */
    listProviderCloudAccessConnections(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listProviderCloudAccessConnectionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a compact, paginated hostname-family catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List observed Site Intelligence naming families
     */
    listSiteIntelligenceNamingFamiliesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceNamingFamilies().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceNamingFamilies().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/naming`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceNamingListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a compact, paginated hostname-family catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List observed Site Intelligence naming families
     */
    listSiteIntelligenceNamingFamilies(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceNamingFamiliesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a compact, paginated list of observed naming review questions for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence naming review questions
     */
    listSiteIntelligenceNamingReviewQuestionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceNamingReviewQuestions().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceNamingReviewQuestions().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/naming/review-questions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceNamingReviewQuestionListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a compact, paginated list of observed naming review questions for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence naming review questions
     */
    listSiteIntelligenceNamingReviewQuestions(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceNamingReviewQuestionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns every site summary, instance summary, interpretation, commonality, and observed-difference statement for the selected immutable snapshot. Follow findingId to retrieve its typed deterministic evidence.
     * List Site Intelligence routing findings
     */
    listSiteIntelligenceRoutingFindingsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceRoutingFindings().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceRoutingFindings().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/routing/findings`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceRoutingFindingListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns every site summary, instance summary, interpretation, commonality, and observed-difference statement for the selected immutable snapshot. Follow findingId to retrieve its typed deterministic evidence.
     * List Site Intelligence routing findings
     */
    listSiteIntelligenceRoutingFindings(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceRoutingFindingsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns every evidence-backed BGP review question for the selected immutable snapshot. Follow findingId to retrieve the typed deterministic observations behind a question.
     * List Site Intelligence routing review questions
     */
    listSiteIntelligenceRoutingReviewQuestionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceRoutingReviewQuestions().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceRoutingReviewQuestions().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/routing/review-questions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceRoutingReviewQuestionListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns every evidence-backed BGP review question for the selected immutable snapshot. Follow findingId to retrieve the typed deterministic observations behind a question.
     * List Site Intelligence routing review questions
     */
    listSiteIntelligenceRoutingReviewQuestions(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceRoutingReviewQuestionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a compact, paginated topology classification catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence topology findings
     */
    listSiteIntelligenceTopologyFindingsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceTopologyFindings().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceTopologyFindings().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/topology/findings`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceTopologyFindingListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a compact, paginated topology classification catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence topology findings
     */
    listSiteIntelligenceTopologyFindings(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceTopologyFindingsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a compact, paginated topology review-question catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence topology review questions
     */
    listSiteIntelligenceTopologyReviewQuestionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceTopologyReviewQuestions().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceTopologyReviewQuestions().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/topology/review-questions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceTopologyReviewQuestionListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a compact, paginated topology review-question catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List Site Intelligence topology review questions
     */
    listSiteIntelligenceTopologyReviewQuestions(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceTopologyReviewQuestionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a compact, paginated observed VLAN catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List observed Site Intelligence VLANs
     */
    listSiteIntelligenceVlansRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listSiteIntelligenceVlans().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling listSiteIntelligenceVlans().');
            }
            const queryParameters = {};
            if (requestParameters['snapshotId'] != null) {
                queryParameters['snapshotId'] = requestParameters['snapshotId'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/intelligence/vlans`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteIntelligenceVlanListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a compact, paginated observed VLAN catalog for the selected immutable snapshot. Omit snapshotId to select the latest completed snapshot.
     * List observed Site Intelligence VLANs
     */
    listSiteIntelligenceVlans(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listSiteIntelligenceVlansRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Paginated list of sites in the organization.
     * List sites
     */
    orgsOrgIdSitesGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            if (requestParameters['before'] != null) {
                queryParameters['before'] = requestParameters['before'];
            }
            if (requestParameters['q'] != null) {
                queryParameters['q'] = requestParameters['q'];
            }
            if (requestParameters['siteType'] != null) {
                queryParameters['siteType'] = requestParameters['siteType'];
            }
            if (requestParameters['connectorClusterId'] != null) {
                queryParameters['connectorClusterId'] = requestParameters['connectorClusterId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SitesListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Paginated list of sites in the organization.
     * List sites
     */
    orgsOrgIdSitesGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdSitesGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates a site in the organization. Returns **201**, the new resource, **Location**, and **ETag**.
     * Create site
     */
    orgsOrgIdSitesPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesPost().');
            }
            if (requestParameters['siteCreate'] == null) {
                throw new runtime.RequiredError('siteCreate', 'Required parameter "siteCreate" was null or undefined when calling orgsOrgIdSitesPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: SiteCreateToJSON(requestParameters['siteCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteFromJSON(jsonValue));
        });
    }
    /**
     * Creates a site in the organization. Returns **201**, the new resource, **Location**, and **ETag**.
     * Create site
     */
    orgsOrgIdSitesPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdSitesPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the connector clusters currently attached to this site. Pagination.
     * List connector clusters attached to a site
     */
    orgsOrgIdSitesSiteIdConnectorClustersGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesSiteIdConnectorClustersGet().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling orgsOrgIdSitesSiteIdConnectorClustersGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            if (requestParameters['before'] != null) {
                queryParameters['before'] = requestParameters['before'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}/connector-clusters`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => ConnectorClustersListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns the connector clusters currently attached to this site. Pagination.
     * List connector clusters attached to a site
     */
    orgsOrgIdSitesSiteIdConnectorClustersGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdSitesSiteIdConnectorClustersGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Requires **If-Match** with the current **ETag**. Returns **204 No Content**.
     * Delete site
     */
    orgsOrgIdSitesSiteIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesSiteIdDelete().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling orgsOrgIdSitesSiteIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifMatch'] != null) {
                headerParameters['If-Match'] = String(requestParameters['ifMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Requires **If-Match** with the current **ETag**. Returns **204 No Content**.
     * Delete site
     */
    orgsOrgIdSitesSiteIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdSitesSiteIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Returns the site resource. Supports cache revalidation via **If-None-Match**.
     * Get site
     */
    orgsOrgIdSitesSiteIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesSiteIdGet().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling orgsOrgIdSitesSiteIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteFromJSON(jsonValue));
        });
    }
    /**
     * Returns the site resource. Supports cache revalidation via **If-None-Match**.
     * Get site
     */
    orgsOrgIdSitesSiteIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdSitesSiteIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Partial update; requires **If-Match** with the current **ETag**. For `settings`, send only the knobs you wish to change—omitted values stay as-is.
     * Update site
     */
    orgsOrgIdSitesSiteIdPatchRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdSitesSiteIdPatch().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling orgsOrgIdSitesSiteIdPatch().');
            }
            if (requestParameters['siteUpdate'] == null) {
                throw new runtime.RequiredError('siteUpdate', 'Required parameter "siteUpdate" was null or undefined when calling orgsOrgIdSitesSiteIdPatch().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (requestParameters['ifMatch'] != null) {
                headerParameters['If-Match'] = String(requestParameters['ifMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/sites/{siteId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"siteId"}}`, encodeURIComponent(String(requestParameters['siteId']))),
                method: 'PATCH',
                headers: headerParameters,
                query: queryParameters,
                body: SiteUpdateToJSON(requestParameters['siteUpdate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => SiteFromJSON(jsonValue));
        });
    }
    /**
     * Partial update; requires **If-Match** with the current **ETag**. For `settings`, send only the knobs you wish to change—omitted values stay as-is.
     * Update site
     */
    orgsOrgIdSitesSiteIdPatch(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdSitesSiteIdPatchRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Azure-only operation. AWS cloud access keeps its existing enabled-on-create flow and does not use this verification endpoint. Uses the subscription scope stored on the organization-owned connection. Caller-supplied cloud credentials, account identifiers, tokens, and targets are not accepted. Azure verification errors use a safe Problem extension named code. Retry only 409/azure_access_pending, 429/verification_busy, 503/azure_rate_limited, or 504/verification_timeout. Authorization, approval, invalid-input, and other failures require user or operator action; they must not be polled indefinitely.
     * Verify a stored Azure cloud access connection
     */
    verifyProviderCloudAccessConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling verifyProviderCloudAccessConnection().');
            }
            if (requestParameters['provider'] == null) {
                throw new runtime.RequiredError('provider', 'Required parameter "provider" was null or undefined when calling verifyProviderCloudAccessConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling verifyProviderCloudAccessConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/{provider}/cloud-access/{connectionId}/verify`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"provider"}}`, encodeURIComponent(String(requestParameters['provider']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudAccessConnectionFromJSON(jsonValue));
        });
    }
    /**
     * Azure-only operation. AWS cloud access keeps its existing enabled-on-create flow and does not use this verification endpoint. Uses the subscription scope stored on the organization-owned connection. Caller-supplied cloud credentials, account identifiers, tokens, and targets are not accepted. Azure verification errors use a safe Problem extension named code. Retry only 409/azure_access_pending, 429/verification_busy, 503/azure_rate_limited, or 504/verification_timeout. Authorization, approval, invalid-input, and other failures require user or operator action; they must not be polled indefinitely.
     * Verify a stored Azure cloud access connection
     */
    verifyProviderCloudAccessConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.verifyProviderCloudAccessConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
}
/**
 * @export
 */
export const OrgsOrgIdSitesGetSiteTypeEnum = {
    OnPrem: 'OnPrem',
    AwsCloud: 'awsCloud',
    AzureCloud: 'azureCloud'
};
/**
 * @export
 */
export const VerifyProviderCloudAccessConnectionProviderEnum = {
    Azure: 'azure'
};
